import type {Dispatch, SetStateAction} from 'react'
import type {ValidationEvent} from '@/products/campaign-forms/cdc-screen-set-wrapper/models'
import type {CampaignData} from '@/models/campaign/campaign-data'
import type {Question} from '@/models/campaign/question'
import {http} from '@/services/http'
import {logError, logMessage, logEvent} from '@/services/logger/client'

export class CampaignInterestHandler {
	selectedOptionsCallbacks: (() => Question | null)[] = []

	injectedElementCallbacks: (() => string[])[] = []

	errorsCallbacks: (() => boolean)[] = []

	selectedOptions: Question[] = []

	injectedElements: string[] = []

	validationEvents: Dispatch<SetStateAction<ValidationEvent>>[] = []

	registerQuestion(
		selectedOptionCallback: () => Question | null,
		injectedElementCallback: () => string[],
		errorsCallback: () => boolean,
		validate: Dispatch<SetStateAction<ValidationEvent>>
	): void {
		this.selectedOptionsCallbacks.push(selectedOptionCallback)
		this.injectedElementCallbacks.push(injectedElementCallback)
		this.errorsCallbacks.push(errorsCallback)
		this.validationEvents.push(validate)
		logMessage('Campaign question registered')
	}

	validate(): void {
		const event: ValidationEvent = {validateNow: true}
		this.validationEvents.forEach((validationEvent) => {
			validationEvent(event)
		})
	}

	getSelectedOptions(): Question[] {
		this.selectedOptions = []
		this.selectedOptionsCallbacks.forEach((callback) => {
			const selection = callback()
			if (selection) {
				this.selectedOptions.push(selection)
			}
		})

		return this.selectedOptions
	}

	getInjectedElements(): string[] {
		this.injectedElements = []
		this.injectedElementCallbacks.forEach((callback) => {
			this.injectedElements = [...this.injectedElements, ...callback()]
		})

		return this.injectedElements
	}

	isValid(): boolean {
		return this.errorsCallbacks.every((hasErrors) => !hasErrors())
	}

	async send(data: CampaignData): Promise<boolean> {
		let campaignData: Question[] = []
		const hasInjectedQuestions = this.getInjectedElements().length > 0

		if (hasInjectedQuestions) {
			// Use injected questions from CDC screenset
			campaignData = this.getSelectedOptions()
		} else {
			// Use formResponse from data if no injected questions
			campaignData = data.formResponse
		}

		if (data.formId && data.cdcProfile.UID) {
			const requestBody: CampaignData = {
				...data,
				formResponse: campaignData,
			}
			const campaignURL = '/api/campaign/interest'
			const startTime = Date.now()
			try {
				const response = await http.post(campaignURL, requestBody, {
					timeout: 60000,
				})

				const duration = Date.now() - startTime

				logEvent({
					name: 'CAMPAIGN_API_PERFORMANCE',
					properties: {
						duration,
						status: response.status,
						timeout: 60000,
					},
				})
				return response.status === 200
			} catch (error) {
				const duration = Date.now() - startTime
				const errorMessage =
					error instanceof Error ? error.message : 'Unknown error'

				logError(
					new Error(
						`Campaign interest submission failed: ${errorMessage}`
					)
				)
				logEvent({
					name: 'CAMPAIGN_INTEREST_SUBMISSION_FAILED',
					properties: {
						error: errorMessage,
						timeout: 60000,
						duration,
					},
				})

				return false
			}
		}
		return false
	}
}
